import type { AdvisorNotification } from "@repo/shared/types";
import { Link } from "@/components/ui/link";
import { useSidebar } from "@/lib/context/sidebar-context";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import { cn, getRelativeTime } from "@/lib/utils";
import { T } from "./notification-item.i18n";
import { useNotificationText } from "./use-notification-text";

type Props = {
  notification: AdvisorNotification;
  /** Called once the item is opened (mark read, close the list). */
  onOpen: (notification: AdvisorNotification) => void;
};

const ROW =
  "flex w-full flex-col items-start gap-0.5 rounded-md px-3 py-2 text-left text-sm transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

/**
 * QL-728 — one notification in the bell's list. A flow notification is a real
 * link to the flow instance; a support reply opens the ticket in the support
 * side panel (support tickets have no page of their own).
 */
export function NotificationItem({ notification, onOpen }: Props) {
  const t = useT(T);
  const text = useNotificationText()(notification);
  const { userLanguage } = useUserLanguage();
  const { openSupportTicket } = useSidebar();
  const unread = notification.readAt === null;
  const { data } = notification;

  const body = (
    <>
      <span className="flex w-full items-center gap-2">
        {unread && (
          <>
            <span aria-hidden className="h-2 w-2 shrink-0 rounded-full bg-brand" />
            <span className="sr-only">{t.unread}: </span>
          </>
        )}
        <span className={cn("truncate", unread ? "font-semibold" : "font-medium")}>
          {text.title}
        </span>
      </span>
      <span className="w-full truncate text-muted-foreground">{text.detail}</span>
      <span className="text-xs text-muted-foreground">
        {getRelativeTime(new Date(notification.createdAt), userLanguage)}
      </span>
    </>
  );

  if (data.ticketId) {
    const ticketId = data.ticketId;
    return (
      <button
        type="button"
        className={ROW}
        onClick={() => {
          onOpen(notification);
          openSupportTicket(ticketId);
        }}
      >
        {body}
      </button>
    );
  }
  if (data.clientId && data.flowInstanceId) {
    return (
      <Link
        to={`/clients/${data.clientId}/flows/${data.flowInstanceId}`}
        className={ROW}
        onClick={() => onOpen(notification)}
      >
        {body}
      </Link>
    );
  }
  return <div className={ROW}>{body}</div>;
}
